import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import { worksData } from '../../mocks/works';

export default function WorksPage() {
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [visibleWorks, setVisibleWorks] = useState<string[]>([]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const categories = ['ALL', 'FILM', 'MV', 'ANIMATION', 'MV'];

  const filteredWorks = selectedCategory === 'ALL'
    ? worksData
    : worksData.filter(work => work.category === selectedCategory);

  useEffect(() => {
    setVisibleWorks([]);
    const timer = setTimeout(() => {
      filteredWorks.forEach((work, index) => {
        setTimeout(() => {
          setVisibleWorks(prev => [...prev, work.id]);
        }, index * 100);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [selectedCategory]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'WORKS | New Age Cinema',
    description: '映像作家 EIJI HOSHINOの映像作品一覧。映画・MV・アニメーション・実験映像など多彩なジャンルの作品をご覧いただけます。',
    url: `${siteUrl}/works`,
    inLanguage: 'ja',
    author: {
      '@type': 'Person',
      name: 'EIJI HOSHINO',
    },
    hasPart: worksData.map(work => ({
      '@type': 'CreativeWork',
      name: work.title,
      url: `${siteUrl}/works/${work.id}`,
      dateCreated: work.year,
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="WORKS | New Age Cinema - EIJI HOSHINO 映像作品一覧"
        description="映像作家 EIJI HOSHINOの映像作品一覧。映画・MV・アニメーション・実験映像など多彩なジャンルの作品をご覧いただけます。"
        keywords="映像作品,映画,MV,アニメーション,実験映像,EIJI HOSHINO,New Age Cinema"
        canonical="/works"
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-24 md:pt-32 px-5 md:px-12 pb-20 md:pb-32">
        {/* Page Title */}
        <div className="max-w-7xl mx-auto mb-12 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end gap-2 md:gap-6 mb-6 md:mb-8">
            <h1 className="text-5xl md:text-8xl font-light tracking-[0.2em] md:tracking-[0.3em] text-black">WORKS</h1>
            <span className="text-xs md:text-sm font-mono text-gray-500 md:pb-3">({filteredWorks.length} Projects)</span>
          </div>
          <div className="w-full md:w-4/5 h-px bg-[#00F0FF]"></div>
        </div>

        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-8 md:gap-16">
          {/* Filter */}
          <div className="md:w-24">
            <div className="flex md:flex-col gap-4 md:gap-8 overflow-x-auto pb-2 md:pb-0 md:sticky md:top-32">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`text-xs md:text-sm font-light tracking-[0.1em] transition-all duration-300 relative whitespace-nowrap cursor-pointer ${
                    selectedCategory === category ? 'text-[#00F0FF]' : 'text-gray-400 hover:text-gray-600'
                  }`}
                  style={{ writingMode: window.innerWidth >= 768 ? 'vertical-rl' : 'horizontal-tb' }}
                >
                  {category}
                  {selectedCategory === category && (
                    <span className="absolute left-0 bottom-0 md:left-0 md:top-0 w-full md:w-1 h-0.5 md:h-full bg-[#00F0FF]"></span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Works Grid */}
          <div className="flex-1">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-10 gap-y-8 md:gap-y-20">
              {filteredWorks.map((work) => (
                <Link
                  key={work.id}
                  to={`/works/${work.id}`}
                  className={`group transition-all duration-500 ${
                    visibleWorks.includes(work.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                >
                  <div className="relative overflow-hidden mb-3 md:mb-6 aspect-square">
                    <img
                      src={work.thumbnail}
                      alt={work.title}
                      className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="text-xs md:text-sm font-light tracking-[0.2em] text-[#00F0FF]">VIEW PROJECT</span>
                    </div>
                  </div>
                  <h3 className="text-sm md:text-base font-light text-black mb-1 md:mb-2">{work.title}</h3>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="font-mono">{work.year}</span>
                    <span className="text-[#00F0FF]">/</span>
                    <span className="font-light">{work.category}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}